/**
 * Design tokens, the single source of truth.
 * All colors, fonts, and spacing values live here.
 * Import from any component: import { FONT, MONO, COLORS } from "../theme/tokens"
 */

// Fonts
export const FONT = "'Source Sans 3', 'Source Sans Pro', sans-serif";
export const MONO = FONT;  // unified, no separate mono font
export const FONT_URL = "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;500;600;700&display=swap";

// Colors (semantic)
// No yellow or orange, only the green/red status palette.
export const COLORS = {
  accent:      "#4B7FFA",
  accentHover: "#3A6EE8",
  accentLight: "#6D96FB",
  accentGlow:  "rgba(75,127,250,.22)",
  accentSubtle:"rgba(75,127,250,.08)",
  success:     "#34D399",
  error:       "#F87171",
  // warning intentionally removed, this project uses green/red only
};

// Dark palette
// Blue-tinted deep darks, not neutral grey. Each surface level is slightly
// lighter and has a subtle blue hue shift. Inspired by Linear, Vercel
// Dashboard, and Grafana 10. Borders are barely-there solids rather than
// rgba, which reads more crisp on OLED.
export const DARK = {
  // Canvas to surfaces: ascending lightness, blue-tinted greys
  bg:           "#111827",   // visibly blue-tinted instead of near-black
  surface0:     "#1A2332",
  surface1:     "#1F2937",   // cards and panels
  surface2:     "#283548",   // interpolated, hover states
  surface3:     "#374151",   // tooltips and popovers

  // Borders, noticeably more visible than before
  border:       "#374151",
  borderStrong: "#4B5563",   // lighter, for hover/focus
  borderSub:    "#1E2940",   // subtle divider

  text1:        "#F3F4F6",
  text2:        "#9CA3AF",
  text3:        "#6B7280",   // muted, slightly lighter than before

  // Scrollbar
  scrollTrack:  "transparent",
  scrollThumb:  "rgba(75,127,250,.18)",

  // Shadows, somewhat lighter to match the brighter surfaces
  shadow:       "0 2px 8px rgba(0,0,0,.5)",
  shadowLg:     "0 16px 48px rgba(0,0,0,.6)",
  shadowXl:     "0 24px 64px rgba(0,0,0,.7)",

  // Sidebar, somewhat lighter than before but darker than bg for contrast
  sidebar:      "#0F1520",
  sidebarBorder:"rgba(75,127,250,.12)",
};

// Light palette (warm neutral: sand, stone, modern enterprise)
export const LIGHT = {
  bg:           "#F5F0EB",       // warm sand off-white, grounded rather than sterile
  surface0:     "#FFFDF9",       // cards, cream white
  surface1:     "#F0EBE4",       // nested surfaces
  surface2:     "#E8E1D8",       // hover states, row highlights
  surface3:     "#DDD5CA",       // selected rows, active states
  border:       "#D4CBBF",       // visible but soft
  borderStrong: "#BFB5A8",       // hover / active border
  borderSub:    "#E2DCD4",       // table dividers, barely there
  text1:        "#1A1714",       // primary text, tuned for maximum readability
  text2:        "#574E44",
  text3:        "#8C8276",
  scrollTrack:  "transparent",
  scrollThumb:  "#C4B9AC",
  shadow:       "0 1px 4px rgba(80,60,30,.08), 0 1px 2px rgba(80,60,30,.05)",
  shadowLg:     "0 10px 24px rgba(80,60,30,.10), 0 4px 8px rgba(80,60,30,.05)",
  shadowXl:     "0 20px 48px rgba(80,60,30,.12), 0 8px 16px rgba(80,60,30,.06)",
  // Sidebar: deep warm charcoal, deliberately not the usual cold slate
  sidebar:      "#2C2520",
  sidebarText:  "#A89B8E",
  sidebarActive:"#F0EBE4",
  sidebarBorder:"#3D342C",
};

// Spacing
export const SPACING = {
  ctrlH:       "36px",
  ctrlHSm:     "30px",
  ctrlHLg:     "40px",
  ctrlRadius:  "8px",
  ctrlRadiusSm:"6px",
  ctrlPx:      "12px",
  ctrlGap:     "8px",
  ctrlFont:    "13px",
  ctrlFontSm:  "12px",
  cardRadius:  "10px",
  cardPx:      "16px",
  cardPy:      "14px",
  sectionGap:  "16px",
  toolbarGap:  "10px",
  sidebarW:    240,
  sidebarCollapsed: 64,
};

// Status colors
// Green/red only, per the project requirement of no yellow/orange status states
export const STATUS_COLORS = {
  COMPLETED: "success",
  STARTED:   "success",
  FAILED:    "error",
  ERROR:     "error",
  PROCESSING:"primary",
  RETRY:     "error",   // degraded, treated as an error state
  ESCALATED: "error",
};
